import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import useJsonLd from '../useJsonLd';
import { SITE_URL } from '../siteData';
import { NewsBlock, findNews, newsDate, newsGallery, newsLink } from '../newsData';
import { mediaUrl } from '../media';
import { useSiteContent } from '../SiteContent';
import NewsGallery from '../components/NewsGallery';
import { withEmphasis } from '../emphasis';
import T from '../components/EditableText';

/** Seitenrelative Adresse zur vollständigen machen; Absolutes bleibt unberührt. */
const absolute = (pathOrUrl: string): string =>
  pathOrUrl.startsWith('http') ? pathOrUrl : `${SITE_URL}${pathOrUrl}`;

const Block: React.FC<{ block: NewsBlock }> = ({ block }) => {
  if (block.type === 'h') {
    return (
      <h2 className="h-block" style={{ marginTop: 26 }}>
        {block.text}
      </h2>
    );
  }
  if (block.type === 'ul' || block.type === 'ol') {
    const items = (block.items ?? []).map((item) => <li key={item}>{withEmphasis(item)}</li>);
    return block.type === 'ol' ? <ol className="prose-list">{items}</ol> : <ul className="prose-list">{items}</ul>;
  }
  return <p>{withEmphasis(block.text ?? '')}</p>;
};

const NewsArtikel: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { news } = useSiteContent();
  const article = findNews(news, slug);

  usePageMeta(
    article ? article.title : 'Beitrag nicht gefunden',
    article ? article.teaser : 'Diesen Beitrag gibt es nicht (mehr).',
    article
      ? { image: mediaUrl(article.image), type: 'article' }
      : { noindex: true },
  );

  // Artikel-Markup samt Brotkrumenpfad — sorgt in der Trefferliste für Datum
  // und den Pfad „Aktuelles › Beitrag“ statt der nackten URL.
  const jsonLd = React.useMemo(() => {
    if (!article) return null;
    const url = `${SITE_URL}${newsLink(article)}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.teaser,
        // Bei hochgeladenen Bildern liefert mediaUrl bereits eine vollständige
        // Adresse; nur die ausgelieferten Dateien brauchen den Website-Namen davor.
        image: absolute(mediaUrl(article.image)),
        datePublished: article.date,
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: 'Seniorenbetreuung Antczak' },
        publisher: { '@id': `${SITE_URL}/#organisation` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Aktuelles', item: `${SITE_URL}/aktuelles` },
          { '@type': 'ListItem', position: 3, name: article.title, item: url },
        ],
      },
    ];
  }, [article]);

  useJsonLd('news-artikel', jsonLd);

  if (!article) {
    return (
      <section className="shell shell--prose centered" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <span className="eyebrow">
          <T k="artikel.fehlt.eyebrow" />
        </span>
        <h1 className="h-hero">
          <T k="artikel.fehlt.titel" />{' '}
          <span className="accent">
            <T k="artikel.fehlt.titel-akzent" />
          </span>
        </h1>
        <p className="lead" style={{ marginBottom: 28 }}>
          <T k="artikel.fehlt.text" />
        </p>
        <Link to="/aktuelles" className="btn btn--primary">
          <T k="artikel.fehlt.cta" />
        </Link>
      </section>
    );
  }

  const gallery = newsGallery(article);
  const others = news.filter((item) => item.slug !== article.slug).slice(0, 3);

  return (
    <>
      <article>
        {/* Kopf */}
        <section className="shell shell--prose" style={{ paddingTop: 48, paddingBottom: 8 }}>
          <Link to="/aktuelles" className="textlink" style={{ fontSize: 15 }}>
            <Icon name="arrow-left" size={16} /> <T k="artikel.zurueck" />
          </Link>
          <h1 className="h-page" style={{ marginBottom: 10 }}>
            {article.title}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--green-dark)', fontWeight: 700, fontSize: 14 }}>
            <Icon name="calendar-check" size={16} />
            <time dateTime={article.date}>{newsDate(article)}</time>
          </div>
        </section>

        {/* Titelbild */}
        <section className="shell" style={{ paddingTop: 24, paddingBottom: 0 }}>
          <div className="media">
            <img
              src={mediaUrl(article.image)}
              alt={article.imageAlt}
              width={1400}
              height={800}
              style={{ width: '100%', maxHeight: 520, objectFit: 'cover' }}
            />
          </div>
        </section>

        {/* Text */}
        <section className="shell shell--prose prose" style={{ paddingTop: 30, paddingBottom: 10 }}>
          {article.body.map((block, index) => (
            <Block key={index} block={block} />
          ))}
        </section>

        {/* Galerie */}
        {gallery.length > 0 && (
          <section className="shell" style={{ paddingTop: 26, paddingBottom: 10 }}>
            <h2 className="h-section" style={{ marginBottom: 16 }}>
              <T k="artikel.galerie.titel" />
            </h2>
            <NewsGallery images={gallery} />
          </section>
        )}
      </article>

      {/* Weitere Beiträge */}
      <section className="shell section" style={{ paddingBottom: 10 }}>
        <h2 className="h-section" style={{ marginBottom: 18 }}>
          <T k="artikel.weitere.titel" />
        </h2>
        <div className="autogrid" style={{ ['--min' as string]: '260px' }}>
          {others.map((item) => (
            <Link
              key={item.slug}
              to={newsLink(item)}
              className="card news-card"
              style={{ padding: 0, overflow: 'hidden', color: 'inherit' }}
            >
              <img
                src={mediaUrl(item.image)}
                alt={item.imageAlt}
                style={{ width: '100%', height: 170, objectFit: 'cover' }}
                width={900}
                height={520}
                loading="lazy"
              />
              <div style={{ padding: '18px 22px 22px' }}>
                <div style={{ color: 'var(--green-dark)', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                  {newsDate(item)}
                </div>
                <div style={{ fontWeight: 600, color: 'var(--ink)', lineHeight: 1.35 }}>
                  {item.title}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
};

export default NewsArtikel;
